package com.videoanalyzer.web.components

import androidx.compose.runtime.Composable
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Maintenance mode page displayed when the app is under maintenance.
 *
 * This page is shown instead of the main app when the "maintenanceMode"
 * feature flag is enabled in ConfigCat.
 */
@Composable
fun MaintenancePage(
    message: String = "We're currently performing maintenance. Please check back soon."
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            minHeight(100.vh)
            backgroundColor(Color("#0f172a"))
            color(Color.white)
            padding(24.px)
            textAlign("center")
        }
    }) {
        // Maintenance icon (wrench/gear)
        Div({
            style {
                fontSize(64.px)
                marginBottom(24.px)
            }
        }) {
            // Using CSS to create a simple gear icon effect
            Span({
                style {
                    display(DisplayStyle.InlineBlock)
                    width(80.px)
                    height(80.px)
                    borderRadius(50.percent)
                    backgroundColor(Color("#E53935"))
                    position(Position.Relative)
                }
            }) {
                // Gear teeth effect
                Span({
                    style {
                        position(Position.Absolute)
                        top(50.percent)
                        left(50.percent)
                        property("transform", "translate(-50%, -50%)")
                        fontSize(40.px)
                    }
                }) {
                    Text("\u2699") // Gear unicode character
                }
            }
        }

        // Logo/Brand
        H1({
            style {
                fontSize(32.px)
                fontWeight(700)
                marginBottom(8.px)
                property("background", "linear-gradient(135deg, #E53935, #C62828)")
                property("-webkit-background-clip", "text")
                property("-webkit-text-fill-color", "transparent")
                property("background-clip", "text")
            }
        }) {
            Text("VideoScore")
        }

        // Maintenance title
        H2({
            style {
                fontSize(24.px)
                fontWeight(600)
                marginBottom(16.px)
                color(Color("#f1f5f9"))
            }
        }) {
            Text("Under Maintenance")
        }

        // Maintenance message
        P({
            style {
                fontSize(16.px)
                color(Color("#94a3b8"))
                maxWidth(500.px)
                lineHeight("1.6")
                marginBottom(32.px)
            }
        }) {
            Text(message)
        }

        // Animated dots to show activity
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(8.px)
            }
        }) {
            repeat(3) { index ->
                Span({
                    style {
                        width(12.px)
                        height(12.px)
                        borderRadius(50.percent)
                        backgroundColor(Color("#E53935"))
                        property("animation", "pulse 1.5s ease-in-out infinite")
                        property("animation-delay", "${index * 0.2}s")
                    }
                })
            }
        }

        // Add keyframe animation via style tag
        Style {
            """
            @keyframes pulse {
                0%, 100% {
                    opacity: 0.3;
                    transform: scale(0.8);
                }
                50% {
                    opacity: 1;
                    transform: scale(1);
                }
            }
            """.trimIndent()
        }

        // Status info
        Div({
            style {
                marginTop(48.px)
                padding(16.px, 24.px)
                backgroundColor(Color("#1e293b"))
                borderRadius(8.px)
                border(1.px, LineStyle.Solid, Color("#334155"))
            }
        }) {
            P({
                style {
                    fontSize(14.px)
                    color(Color("#64748b"))
                    margin(0.px)
                }
            }) {
                Text("We're working hard to improve your experience.")
            }
            P({
                style {
                    fontSize(14.px)
                    color(Color("#64748b"))
                    margin(8.px, 0.px, 0.px)
                }
            }) {
                Text("For urgent matters, email us at ")
                A(
                    href = "mailto:support@videoscore.io",
                    attrs = {
                        style {
                            color(Color("#0EA5E9"))
                            property("text-decoration", "none")
                        }
                    }
                ) {
                    Text("support@videoscore.io")
                }
            }
        }
    }
}
